import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/react-hooks';
import { Button } from '@shopify/polaris';
import ListProductMutation from 'src/mutations/ListProductMutation';
import ProductGridLoadingState from 'src/components/ProductGridLoadingState';
import CourseCard from 'src/components/CourseCard';
import BannerMessages from 'src/components/BannerMessages';
import styleConstants from 'src/styles/constants';
import PAGINATION_LABEL from './constants';

const ProductGrid = ({
  handleListProductMutation,
  isLoadingMore,
  isFinishedLoading,
  products,
  onLoadMore,
  hasNextToken,
}) => {
  const [listProduct, { loading, error }] = useMutation(ListProductMutation, {
    onCompleted: () => {
      handleListProductMutation();
    },
  });

  if (loading) return <ProductGridLoadingState />;
  if (error) {
    const errorMessage = error.message.replace('GraphQL error:', '').trim();
    return <BannerMessages message={errorMessage} />;
  }

  return (
    <>
      <div className="product-grid__container">
        <div className="product-grid__cards-container">
          {products.map((product) => (
            <CourseCard
              imageUrl={product.imageUrl}
              instructor={product.instructor}
              key={product.thinkificProductId}
              listProduct={listProduct}
              name={product.name}
              publishedStatus={product.status}
              shopifyProductId={product.shopifyProductId}
              thinkificCourseId={product.thinkificCourseId}
              thinkificProductId={product.thinkificProductId}
            />
          ))}
        </div>
        <div className="product-grid__footer">
          {hasNextToken && !isFinishedLoading && (
            <div className="text-center loading-indicator">
              <Button
                loading={isLoadingMore}
                size="large"
                primary
                onClick={onLoadMore}
              >
                {PAGINATION_LABEL.LOAD_MORE}
              </Button>
            </div>
          )}
          <div role="status">
            {isFinishedLoading && (
              <p className="Polaris-TextStyle--variationSubdued text-center">
                {PAGINATION_LABEL.FINISHED_LOADING}
              </p>
            )}
            {isLoadingMore && (
              <p className="sr-only">{PAGINATION_LABEL.LOADING}</p>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .product-grid__cards-container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          grid-gap: ${styleConstants.spacing.five};
        }
        .product-grid__cards-container :global(.Polaris-Card + .Polaris-Card) {
          margin-top: 0;
        }
        .product-grid__cards-container :global(.Polaris-Link) {
          text-decoration: none;
        }
        .product-grid__footer {
          display: flex;
          justify-content: center;
          margin-top: 40px;
          min-height: 85px;
          align-items: center;
        }
        .loading-text {
          margin-bottom: ${styleConstants.spacing.four};
        }
        .loading-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .sr-only {
          clip: rect(0 0 0 0);
          clip-path: inset(50%);
          height: 1px;
          overflow: hidden;
          position: absolute;
          white-space: nowrap;
          width: 1px;
        }
      `}</style>
    </>
  );
};

ProductGrid.propTypes = {
  handleListProductMutation: PropTypes.func.isRequired,
  hasNextToken: PropTypes.bool.isRequired,
  isFinishedLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      __typename: PropTypes.string,
      imageUrl: PropTypes.string.isRequired,
      instructor: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      shopifyProductId: PropTypes.string,
      status: PropTypes.string.isRequired,
      thinkificCourseId: PropTypes.string.isRequired,
      thinkificProductId: PropTypes.string.isRequired,
    })
  ).isRequired,
  onLoadMore: PropTypes.func.isRequired,
};

export default ProductGrid;
